import { Descriptions, Empty, Tag, Typography } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'
import React from 'react'
import { DATE_TIME_FORMAT } from '../../constants'

const { Text, Paragraph } = Typography

// Recorded for every row but not worth repeating as fields — `summary` is
// rendered as the headline, and route/operation belong in the technical block.
const HANDLED_METADATA_KEYS = new Set(['summary', 'area', 'route', 'operation', 'payload'])

/**
 * Turns a payload key into a label a person reads.
 *
 * The keys come from request bodies, so they are camelCase and occasionally
 * snake_case. Neither reads well in a table an admin is scanning during an
 * incident.
 * @param {string} key Raw key.
 * @returns {string} Display label.
 */
const humanise = (key) =>
  String(key)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/^./, (character) => character.toUpperCase())

/**
 * Renders a value the way a person reads it, not the way JSON prints it.
 *
 * Arrays of objects are the common shape here — a list of specialisations, a
 * list of student ids — and printing them raw is what made the old panel
 * unreadable. A count says more than 400 characters of uuid.
 * @param {*} value Any stored value.
 * @returns {string} Display string.
 */
const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (!value.length) return 'None'
    if (value.every((item) => typeof item !== 'object' || item === null)) {
      return value.join(', ')
    }
    return `${value.length} ${value.length === 1 ? 'item' : 'items'}`
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/**
 * The expanded row: what happened, what changed, and the evidence behind it.
 *
 * Ordered by what a reader needs first — the sentence, then the field-level
 * diff, then the submitted values, and only then the endpoint that carried it.
 * The technical detail is kept because an audit row has to be verifiable, but
 * it is no longer the first thing on the screen.
 */
const AuditChanges = ({ record }) => {
  const changes = record?.changes
  const metadata = record?.metadata
  const changeEntries = changes ? Object.entries(changes) : []
  const payload = metadata?.payload
  const payloadEntries =
    payload && typeof payload === 'object' && !Array.isArray(payload) ? Object.entries(payload) : []
  const extraEntries = metadata
    ? Object.entries(metadata).filter(([key]) => !HANDLED_METADATA_KEYS.has(key))
    : []

  const hasAnything =
    metadata?.summary ||
    changeEntries.length ||
    payloadEntries.length ||
    extraEntries.length ||
    metadata?.route

  if (!hasAnything) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No further detail was recorded for this entry"
      />
    )
  }

  return (
    <div style={{ padding: '4px 0' }}>
      {/* Every entry states its own time. The table column shows it too, but an
          expanded row is what gets screenshotted and pasted into a ticket. */}
      <Paragraph style={{ marginBottom: 12 }}>
        {metadata?.summary ? <Text strong>{metadata.summary}</Text> : null}
        <Text type="secondary" style={{ marginLeft: metadata?.summary ? 8 : 0 }}>
          {record?.occurredAt
            ? moment(record.occurredAt).utcOffset('+05:30').format(DATE_TIME_FORMAT)
            : '—'}{' '}
          IST
        </Text>
        {metadata?.area ? (
          <Tag style={{ marginLeft: 8 }}>{metadata.area}</Tag>
        ) : null}
      </Paragraph>

      {changeEntries.length > 0 && (
        <Descriptions title="What changed" size="small" column={1} bordered style={{ marginBottom: 16 }}>
          {changeEntries.map(([field, change]) => (
            <Descriptions.Item key={field} label={humanise(field)}>
              <Text delete type="secondary">
                {formatValue(change?.from)}
              </Text>
              <Text strong style={{ margin: '0 8px' }}>
                →
              </Text>
              <Text>{formatValue(change?.to)}</Text>
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {payloadEntries.length > 0 && (
        <Descriptions title="Submitted values" size="small" column={2} bordered style={{ marginBottom: 16 }}>
          {payloadEntries.map(([key, value]) => (
            <Descriptions.Item key={key} label={humanise(key)}>
              {formatValue(value)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {extraEntries.length > 0 && (
        <Descriptions title="Context" size="small" column={2} bordered style={{ marginBottom: 16 }}>
          {extraEntries.map(([key, value]) => (
            <Descriptions.Item key={key} label={humanise(key)}>
              {formatValue(value)}
            </Descriptions.Item>
          ))}
        </Descriptions>
      )}

      {metadata?.route && (
        <Descriptions title="Technical" size="small" column={2} bordered>
          <Descriptions.Item label="Endpoint">
            <Text code>
              {metadata.operation} {metadata.route}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Request">
            <Text type="secondary">{record?.requestId || '—'}</Text>
          </Descriptions.Item>
        </Descriptions>
      )}
    </div>
  )
}

AuditChanges.propTypes = {
  record: PropTypes.shape({
    changes: PropTypes.object,
    metadata: PropTypes.object,
    occurredAt: PropTypes.string,
    requestId: PropTypes.string,
  }).isRequired,
}

export default AuditChanges
